import {
    useEffect,
    useRef
} from "react";
import {
    Platform
} from "react-native";
import type {
    AnimatedIconHostConstantType
} from "./type";
import {
    ANIMATED_ICON_DISABLED_SELECTOR,
    ANIMATED_ICON_HOST_SELECTOR
} from "./constant";

const isHostNode = (node: unknown): node is Element => {
    return typeof node === "object" && node !== null && "closest" in node && typeof node.closest === "function";
};

const isTouchEvent = (event: Event): boolean => {
    return "pointerType" in event && event.pointerType === "touch";
};

const isSecondaryButtonEvent = (event: Event): boolean => {
    return "button" in event && event.button !== 0;
};

const isActivationEvent = (event: Event): boolean => {
    if(!("key" in event) || ("repeat" in event && event.repeat === true)) {
        return false;
    }

    return event.key === "Enter" || event.key === " ";
};

const useIconHost = ({
    onTrigger,
    trigger,
    nodeRef
}: AnimatedIconHostConstantType) => {
    const onTriggerRef = useRef(onTrigger);

    useEffect(() => {
        onTriggerRef.current = onTrigger;
    });

    useEffect(() => {
        if(Platform.OS !== "web" || (trigger !== "hover" && trigger !== "press")) {
            return;
        }

        const node = nodeRef.current;

        if(!isHostNode(node)) {
            return;
        }

        const host = node.closest(ANIMATED_ICON_HOST_SELECTOR) ?? node;

        const onPointerEnter = (event: Event) => {
            if(isTouchEvent(event) || host.matches(ANIMATED_ICON_DISABLED_SELECTOR)) {
                return;
            }

            onTriggerRef.current();
        };

        const onPointerDown = (event: Event) => {
            if(isSecondaryButtonEvent(event) || host.matches(ANIMATED_ICON_DISABLED_SELECTOR)) {
                return;
            }

            onTriggerRef.current();
        };

        const onKeyDown = (event: Event) => {
            if(!isActivationEvent(event) || host.matches(ANIMATED_ICON_DISABLED_SELECTOR)) {
                return;
            }

            onTriggerRef.current();
        };

        if(trigger === "hover") {
            host.addEventListener("pointerenter", onPointerEnter);

            return () => {
                host.removeEventListener("pointerenter", onPointerEnter);
            };
        }

        host.addEventListener("pointerdown", onPointerDown);

        host.addEventListener("keydown", onKeyDown);

        return () => {
            host.removeEventListener("pointerdown", onPointerDown);

            host.removeEventListener("keydown", onKeyDown);
        };
    }, [
        trigger
    ]);
};
export default useIconHost;
